import { MarketCode } from '../types/market'
import { PRODUCT_HANDLE, SHOP_ORIGIN, VARIANT_ID } from '../data/markets'

export interface ShopifyPrice {
  /** Currency Shopify serves for this market. */
  currency: string
  /** Exact localized amount Shopify charges. */
  price: number
  /** Exact localized compare-at amount, when the market has one. */
  compareAt: number | null
}

type InjectedPrices = Partial<Record<MarketCode, ShopifyPrice>>

declare global {
  interface Window {
    /**
     * Optional Liquid-injected prices, e.g.
     *   window.__SHOPIFY_MARKET_PRICES__ = {
     *     CO: { currency: {{ cart.currency.iso_code | json }},
     *           price: {{ variant.price | divided_by: 100.0 }},
     *           compareAt: {{ variant.compare_at_price | divided_by: 100.0 }} }
     *   }
     * When present this takes priority: it is Shopify's own rendered value.
     */
    __SHOPIFY_MARKET_PRICES__?: InjectedPrices
  }
}

function fromInjected(code: MarketCode): ShopifyPrice | null {
  const injected = typeof window !== 'undefined' ? window.__SHOPIFY_MARKET_PRICES__ : undefined
  const entry = injected?.[code]
  if (!entry || typeof entry.price !== 'number') return null
  return {
    currency: entry.currency,
    price: entry.price,
    compareAt: typeof entry.compareAt === 'number' ? entry.compareAt : null,
  }
}

interface ProductJsonVariant {
  id: number | string
  price: number | string
  compare_at_price: number | string | null
}

interface ProductJson {
  variants?: ProductJsonVariant[]
  price?: number | string
  compare_at_price?: number | string | null
}

/** Shopify product JSON returns integer cents/minor units. */
function toAmount(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return null
  return n / 100
}

/**
 * Reads the exact localized price Shopify serves for `code`.
 *
 * Order of truth:
 *   1. Liquid-injected market prices (Shopify's own rendered amounts).
 *   2. The storefront product JSON for that country, which is rendered in the
 *      market's currency by Shopify Markets.
 * Returns null when neither is reachable, so the caller keeps the last known
 * Shopify values instead of inventing a converted amount.
 */
export async function fetchShopifyPrice(
  code: MarketCode,
  signal?: AbortSignal,
): Promise<ShopifyPrice | null> {
  const injected = fromInjected(code)
  if (injected) return injected

  // `country` is the only valid market param here; passing a country code as
  // `currency` made Shopify reject the request.
  const url = `${SHOP_ORIGIN}/products/${PRODUCT_HANDLE}.js?country=${code}`

  try {
    // Same-origin credentials keep the visitor's Shopify market session, so the
    // amount returned is exactly the one the cart will show.
    const res = await fetch(url, { signal, credentials: 'same-origin' })
    if (!res.ok) return null

    const data = (await res.json()) as ProductJson
    const variant =
      data.variants?.find((v) => String(v.id) === VARIANT_ID) ?? data.variants?.[0]

    const price = toAmount(variant?.price ?? data.price)
    if (price === null) return null

    const currencyHeader = res.headers.get('x-shopify-currency')

    return {
      currency: currencyHeader ?? '',
      price,
      compareAt: toAmount(variant?.compare_at_price ?? data.compare_at_price),
    }
  } catch {
    return null
  }
}
